import { AccordionRoutesEnum } from "../../../../types/AccordionRoutes";
import Accordion from "../../components/Accordion";
import ConsentCard from "../../components/ConsentCard";
import { useStore } from "../../../../store/store";
import textContent from "./text";
import { BtnContainer, Consent, ConsentContainer, ConsentDescription, ConsentHeadline, ConsentTitle, Container, InfoIcon, InfoIconContainer, ModelLink} from "./YourConsentStep.style";
import { useMemo, useState } from "react";
import Tooltip from "../../../../components/Tooltip";
import Modal from "../../components/Modal";
import Analytics from "../../../../utils/analytics";
import { TrackingFlowTypeEnum } from "../../../../types/TrackingFlowTypeEnum";
import { PaymentFrequencyTypeEnum } from "../../../../types/PaymentFrequencyTypeEnum";
import { fetchSignupDate } from "../../../../utils/api";
import Button from "../../components/Button";
import { ButtonBackgroundColorType } from "../../components/Button/Button";

enum ConsentType {
  BOTH = "BOTH",
  UNION = "UNION",
  UNEMPLOYMENT_BENEFITS = "UNEMPLOYMENT_BENEFITS",
}

interface Props {
  step2: AccordionRoutesEnum;
}

export default function ConsentStep({step2}: Props) {
  const payload = useStore.usePayload();
  const setPayload = useStore.useSetPayload();
  const setCompletedAccordionRoute = useStore.useSetCompletedAccordionRoute();
  const setAccordionRoute = useStore.useSetAccordionRoute();
  const setBsPaymentFee = useStore.useSetBsPaymentFee();
  const setMobilePayPaymentFee = useStore.useSetMobilePayPaymentFee();
  const translations = useStore.useTranslations();
  /*const productConsentSet = (payload.wantsUnion && Boolean(payload.consents?.union?.aseCooperation)) || (payload.wantsUnemploymentBenefits && Boolean(payload.consents?.unemploymentBenefits?.aseCooperation));
  const partnerConsentSet = (payload.wantsUnemploymentBenefits && Boolean(payload.consents?.unemploymentBenefits?.member) && Boolean(payload.consents?.unemploymentBenefits?.partner)) || (payload.wantsUnion && Boolean(payload.consents?.union?.member) && Boolean(payload.consents?.union?.partner));
  const [productConsent, setProductConsent] = useState(productConsentSet);
  const [partnerConsent, setPartnerConsent] = useState(partnerConsentSet); */
  const [productConsent, setProductConsent] = useState<boolean | undefined>(undefined);
  const [partnerConsent, setPartnerConsent] = useState<boolean | undefined>(undefined);
  const [salaryInsuranceConsent, setSalaryInsuranceConsent] = useState<boolean | undefined>(undefined);
  const [showPartnerList, setShowPartnerList] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showInfoProductConsent, setShowInfoProductConsent] = useState(false);
  const [showInfoPartnerConsent, setShowInfoPartnerConsent] = useState(false);
  const [showInfoSalaryInsuranceConsent, setShowInfoSalaryInsuranceConsent] = useState(false);

  const wantsSalaryInsuranceConsent = payload.wantsSalaryInsurance || payload.wantsSalaryInsuranceAndUnion;

  const setConsentPayload = () => {
    setPayload({
      consents: {
        union: {
          aseCooperation: payload.wantsUnion && productConsent,
          member: payload.wantsUnion && partnerConsent,
          partner:payload.wantsUnion && partnerConsent,
        },
        unemploymentBenefits: {
          aseCooperation: payload.wantsUnemploymentBenefits && productConsent,
          member: payload.wantsUnemploymentBenefits && partnerConsent,
          partner: payload.wantsUnemploymentBenefits && partnerConsent,
        },
        salaryInsurance: {
          aseCooperation: Boolean(wantsSalaryInsuranceConsent) && salaryInsuranceConsent,
        },
      },
    });
  };

  const consentType = useMemo(() => {
    if (payload.wantsUnion && payload.wantsUnemploymentBenefits) {
      return ConsentType.BOTH;
    } else if (!payload.wantsUnion && payload.wantsUnemploymentBenefits) {
      return ConsentType.UNEMPLOYMENT_BENEFITS;
    } else if (payload.wantsUnion && !payload.wantsUnemploymentBenefits) {
      return ConsentType.UNION;
    } else {
      return ConsentType.BOTH;
    }
  }, [payload]);

  const text = useMemo(() => {
    switch (consentType) {
      case ConsentType.BOTH:
        return textContent.wantBoth;
      case ConsentType.UNEMPLOYMENT_BENEFITS:
        return textContent.wantUnemploymentBenefits;
      case ConsentType.UNION:
        return textContent.wantUnion;
    }
  }, [consentType]);

  const box2TextComponent = useMemo(() => {
    return (
      <span>
        Jeg vil gerne høre om spændende medlemstilbud og nyheder fra {" "}
        <Tooltip isBlockStyle={false} content="<p>Ase består af følgende foreninger:</p><ul><li>Ase a-kasse</li><li>Fagforeningen Ase Lønmodtager</li><li>Ase Selvstændig</li></ul>">
          <span><u>Ase</u></span>
        </Tooltip> {" "}
        og deres fordelspartnere. Det er altid {" "}
        <Tooltip isBlockStyle={false} content="<p>Ase består af følgende foreninger:</p><ul><li>Ase a-kasse</li><li>Fagforeningen Ase Lønmodtager</li><li>Ase Selvstændig</li></ul>">
          <span><u>Ase</u></span>
        </Tooltip> {" "} 
        der kontakter mig. Se listen over fordelspartnere {" "}
        <span style={{ cursor: "pointer" }} onClick={() => setShowPartnerList(true)}>
          <u>her</u>
        </span>
        .
      </span>
    );
  }, []);

  const cooperationReadMoreText = useMemo(() => {
    return (
      <span>
        <p><b><u>Fagforeningen deler oplysninger om dit medlemskab med a-kassen</u></b></p>
        <p>Formålet med delingen er at gøre det muligt for a-kassen at kende til dit medlemskab, så den kan sende dig videre til fagforeningen, hvis der er behov for fagretlig rådgivning i forbindelse med en ansøgning om dagpenge eller ved øvrige henvendelser om din arbejdssituation.</p>
        <p><b><u>A-kassen deler oplysninger om dit medlemskab med fagforeningen</u></b></p>
        <p>Formålet med delingen er at gøre det muligt for fagforeningen at kende til dit medlemskab, så den kan sende dig videre til a-kassen, hvis der er behov for rådgivning om ret til dagpenge og andre ydelser eller ved øvrige henvendelser om din arbejdssituation.</p>
        <p>Du kan altid tilbagekalde dit samtykke på MitAse.</p>
      </span>
    );
  }, []);

  const getSignupDate = () => {
    const fetch = async () => {
      try {
        const response = await fetchSignupDate(payload);
        setPayload({ signupDate: response.data.signupdate });
      } catch (err) {
        setShowErrorModal(true);
      }
    };

    if (payload) {
      fetch();
    }
    };

//BsPaymentFee set for next step because of OI splittest, it used to be on the paymentStep
const bsPaymentFee = () => {
    if (payload.paymentFrequency === PaymentFrequencyTypeEnum.Quarterly && payload.wantsUnemploymentBenefits) {
      setBsPaymentFee(Number(translations?.paymentStep.bs.quarterlyServiceFee))
    } else if (payload.paymentFrequency === PaymentFrequencyTypeEnum.Monthly && payload.wantsUnemploymentBenefits) {
      setBsPaymentFee(Number(translations?.paymentStep.bs.monthlyServiceFee))
    } else {
      setBsPaymentFee(0)
    }
  }
//BSPaymentFee end

//MobilepayPaymentFee set for next step because of OI splittest, it used to be on the paymentStep
const mobilePayPaymentFee = () => {
    if (payload.paymentFrequency === PaymentFrequencyTypeEnum.Quarterly && payload.wantsUnemploymentBenefits) {
      setMobilePayPaymentFee(Number(translations?.paymentStep.mobilePay.quarterlyServiceFee))
    } else if (payload.paymentFrequency === PaymentFrequencyTypeEnum.Monthly && payload.wantsUnemploymentBenefits) {
      setMobilePayPaymentFee(Number(translations?.paymentStep.mobilePay.monthlyServiceFee))
    } else {
      setMobilePayPaymentFee(0)
    }
  }
//MobilepayPaymentFee end


const stepComplete = () => {
  if (wantsSalaryInsuranceConsent && salaryInsuranceConsent === undefined) {
    return false;
  }
  if(productConsent !== undefined && partnerConsent !== undefined) {
    return true
  } else {
    return false
  }
}

 return (
  <>
   <Accordion 
      title={translations?.consentStep.title}
      step={AccordionRoutesEnum.ConsentStep}
      onNext={() => {
        setConsentPayload()
        
        //Add tracking
        // Analytics.getInstance().trackAllConsent("added", payload.consents, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);

        setCompletedAccordionRoute(AccordionRoutesEnum.ConsentStep)
        setAccordionRoute(AccordionRoutesEnum.PaymentStep, payload, step2 === AccordionRoutesEnum.ProductStepV2 ? TrackingFlowTypeEnum.VerticalV2 : TrackingFlowTypeEnum.Vertical);
        window.scrollTo({top: 0, left: 0, behavior: "smooth"});

        getSignupDate();
        bsPaymentFee();
        mobilePayPaymentFee();
      }}
      stepComplete={stepComplete()}
      previousStep={AccordionRoutesEnum.InformationStep}
      headerImg="/assets/images/order-approve-outline-rounded.svg"
      step2={step2}
  > 
    <Container>   
   {/*  <ConsentCard 
        title="Hurtigere svar og bedre hjælp"
        description={Object.values(text.box1Text).join("")}
        isChecked={productConsent} 
        hasInfoIcon={true}
        modalText={Object.values(text.box1ModalText).join("")}
        modalTextH1="Samarbejde"
        onClick={() => setProductConsent(!productConsent)}
        onKeyDown={(event) => keyDownProductConsent(event)}
        iconSrc={productConsent ? "/assets/images/cooperation-white.png" : "/assets/images/cooperation-black.png"}
      />
      <ConsentCard 
        title="Stærke personlige tilbud"
        descriptionComponent={box2TextComponent}
        isChecked={partnerConsent} 
        hasInfoIcon={true}
        modalText={Object.values(text.box2ModalText).join("")}
        modalTextH1="Stærke personlige tilbud"
        onClick={() => setPartnerConsent(!partnerConsent)}
        onKeyDown={(event) => keyDownPartnerConsent(event)}
        iconSrc={partnerConsent ? "/assets/images/offer-white.png" : "/assets/images/offer-black.png"}
      /> */}
 
        <InfoIconContainer>
          <ConsentTitle>Ja tak til hurtigere hjælp!</ConsentTitle>
          {/* <InfoIcon onClick={() => setShowInfoProductConsent(!showInfoProductConsent)} /> */}
        </InfoIconContainer>
        <ConsentDescription dangerouslySetInnerHTML={{ __html: text.box1Text }} />
        <ModelLink onClick={() => setShowInfoProductConsent(!showInfoProductConsent)}>Læs mere</ModelLink>
        <Modal modalText={Object.values(text.box1ModalText).join("")} modalTexth1="Samarbejde" modalBlock={false} showModal={showInfoProductConsent} onClose={() => setShowInfoProductConsent(false)} />
        <BtnContainer>
            <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={productConsent} 
                onClick={() => setProductConsent(true)}
              >
                Ja
              </Button>
              <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={productConsent === false} 
                onClick={() => setProductConsent(false)}
              >
                Nej
              </Button> 
        </BtnContainer>
        <InfoIconContainer>
          <ConsentTitle>Ja tak til gode tilbud og nyheder!</ConsentTitle>
          {/* <InfoIcon onClick={() => setShowInfoPartnerConsent(!showInfoPartnerConsent)} /> */}
        </InfoIconContainer>
        <ConsentDescription>{box2TextComponent}</ConsentDescription>
        <ModelLink onClick={() => setShowInfoPartnerConsent(!showInfoPartnerConsent)}>Læs mere</ModelLink>
        <Modal modalText={Object.values(text.box2ModalText).join("")} modalTexth1="Stærke personlige tilbud" modalBlock={false} showModal={showInfoPartnerConsent} onClose={() => setShowInfoPartnerConsent(false)} />
        <BtnContainer>
            <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={partnerConsent} 
                onClick={() => setPartnerConsent(true)}
              >
                Ja
              </Button>
              <Button 
                bgColor={ButtonBackgroundColorType.Blue} 
                blockButton={true} 
                isChecked={partnerConsent === false} 
                onClick={() => setPartnerConsent(false)}
              >
                Nej
              </Button> 
        </BtnContainer>

        {wantsSalaryInsuranceConsent && (
          <>
            <InfoIconContainer>
              <ConsentTitle>Ja tak til deling af oplysninger om min lønsikring!</ConsentTitle>
            </InfoIconContainer>
            <ConsentDescription dangerouslySetInnerHTML={{ __html: textContent.wantSalaryInsuranceAndUnion.box1Text }} />
            <ModelLink onClick={() => setShowInfoSalaryInsuranceConsent(!showInfoSalaryInsuranceConsent)}>Læs mere</ModelLink>
            <Modal modalText={Object.values(textContent.wantSalaryInsuranceAndUnion.box1ModalText).join("")} modalTexth1="Samarbejde" modalBlock={false} showModal={showInfoSalaryInsuranceConsent} onClose={() => setShowInfoSalaryInsuranceConsent(false)} />
            <BtnContainer>
                <Button 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={true} 
                    isChecked={salaryInsuranceConsent} 
                    onClick={() => setSalaryInsuranceConsent(true)}
                  >
                    Ja
                  </Button>
                  <Button 
                    bgColor={ButtonBackgroundColorType.Blue} 
                    blockButton={true} 
                    isChecked={salaryInsuranceConsent === false} 
                    onClick={() => setSalaryInsuranceConsent(false)}
                  >
                    Nej
                  </Button> 
            </BtnContainer>
          </>
        )}

      <ConsentContainer>
        <Consent>Du kan til enhver tid trække dit samtykke tilbage på MitAse.dk eller ved at kontakte os via e-mail: ase@ase.dk</Consent>
        <Consent>Hos Ase respekterer vi dit privatliv, og beskyttelsen af dine oplysninger er vigtigt for os. <a href="https://www.ase.dk/cookie-privatliv-og-persondata/privatlivsbetingelser" target="_blank"><u>Læs mere her.</u></a></Consent>
      </ConsentContainer>
    </Container>
  </Accordion>
  <Modal modalText={Object.values(textContent.partnerPolicy).join("")} modalTexth1="Ases fordelspartnere" modalBlock={false} showModal={showPartnerList} onClose={() => setShowPartnerList(false)} />
  <Modal showModal={showErrorModal} modalText={translations?.summaryStep.errorMessage ?? ""} modalBlock={false} onClose={() => setShowErrorModal(false)}></Modal>
</>
 );
}